<script lang="ts">
	import { Icon, LinearGauge, Text } from '@Shelly/shelly-design-system';
	import { convert } from 'src/helpers/utilities';
	import type { PeakPowerOverviewModel } from '../helpers/energy_peak_helpers';
	import EmPeakDeviceAvatar from './EmPeakDeviceAvatar.svelte';

	export let model: PeakPowerOverviewModel;

	const timeOnly = (value: string | undefined): string => value?.split(' ')[0] || '—';
	const formatWatts = (watts: number): string => convert(Math.abs(watts), 'W');
</script>

<div class="em-peak-overview">
	<div class="em-peak-overview-summary">
		<div class="em-peak-overview-summary-item em-peak-overview-summary-peak">
			<Text styleType="h1" class="text-600">{model.hero.live?.value ?? '—'}</Text>
			<Text styleType="body-small" class="text-color-muted">Highest live peak</Text>
			<Text styleType="body-small" class="text-color-muted">
				{model.hero.deviceName} · {model.hero.label} · {timeOnly(model.hero.live?.time)}
			</Text>
		</div>
		<div class="em-peak-overview-summary-item em-peak-overview-summary-difference">
			<div class:em-peak-overview-warning={model.hero.differenceWatts > 0}>
				{#if model.hero.differenceWatts > 0}
					<Icon icon="fas fa-triangle-exclamation" size="s" />
				{:else}
					<Icon icon="fas fa-circle-check" size="s" />
				{/if}
				<Text styleType="h2" class="text-600">
					{model.hero.differenceWatts >= 0 ? 'Above average' : 'Below average'}
				</Text>
			</div>
			<Text styleType="body-small" class="text-color-muted">
				{formatWatts(model.hero.differenceWatts)} difference
			</Text>
		</div>
		<div class="em-peak-overview-summary-item em-peak-overview-summary-peak">
			<Text styleType="h1" class="text-600">{model.hero.avg5?.value ?? '—'}</Text>
			<Text styleType="body-small" class="text-color-muted">Highest 5 min avg</Text>
			<Text styleType="body-small" class="text-color-muted">
				{model.hero.label} · {timeOnly(model.hero.avg5?.time)}
			</Text>
		</div>
	</div>

	<div class="em-peak-overview-list">
		{#each model.rows.slice(0, 5) as row (row.id)}
			<div
				class="em-peak-overview-row"
				class:em-peak-overview-row-strongest={row.id === model.hero.id}
			>
				<div class="em-peak-overview-source">
					<EmPeakDeviceAvatar device={row.device} size="m" />
					<div>
						<Text styleType="h2" class="text-600">{row.label}</Text>
						<Text styleType="miniature" class="text-color-muted">{row.deviceName}</Text>
					</div>
				</div>
				<div class="em-peak-overview-metric">
					<Text styleType="miniature" class="text-color-muted">Live peak</Text>
					<Text styleType="h2" class="text-600">{row.live?.value ?? '—'}</Text>
				</div>
				<div class="em-peak-overview-metric">
					<Text styleType="miniature" class="text-color-muted">Avg 5 min</Text>
					<Text styleType="h2" class="text-600">{row.avg5?.value ?? '—'}</Text>
				</div>
				<Text styleType="body-small" class="text-color-muted">
					{timeOnly(row.live?.time)}
				</Text>
				<div class="em-peak-overview-gauge">
					<LinearGauge
						type="incremented"
						size="s"
						minValue={0}
						maxValue={model.maxWatts}
						hideMinMaxValue
						incrementedValue={{
							value: row.liveWatts,
							color: row.id === model.hero.id ? '--color-warning' : '--color-primary',
						}}
						a11yProps={{
							incrementedValueAriaLabel: `${row.deviceName} ${row.label} ${row.live?.value ?? ''}`,
						}}
					/>
					<Text
						styleType="body"
						class={row.id === model.hero.id
							? 'em-peak-overview-percent-strongest'
							: 'text-color-muted'}
					>
						{Math.round(row.percent)}%
					</Text>
				</div>
			</div>
		{/each}
	</div>
</div>

<style>
	.em-peak-overview {
		display: flex;
		flex-direction: column;
		min-width: 0;
		container-type: inline-size;
		container-name: peak-overview;
	}

	.em-peak-overview-source,
	.em-peak-overview-gauge,
	.em-peak-overview-summary-difference > div {
		display: flex;
		align-items: center;
		gap: var(--spacing-s);
		min-width: 0;
	}

	.em-peak-overview-summary {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		border-bottom: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-peak-overview-summary-item {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-xs);
		min-width: 0;
		padding: var(--spacing-m);
		text-align: center;
	}

	.em-peak-overview-summary-item + .em-peak-overview-summary-item {
		border-left: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-peak-overview-summary-peak,
	.em-peak-overview-warning,
	:global(.em-peak-overview-percent-strongest) {
		color: var(--color-warning);
	}

	.em-peak-overview-list {
		display: flex;
		flex-direction: column;
	}

	.em-peak-overview-row {
		display: grid;
		grid-template-columns: minmax(150px, 1fr) auto auto auto minmax(180px, 1.5fr);
		align-items: center;
		gap: var(--spacing-m);
		min-width: 0;
		padding: var(--spacing-s) var(--spacing-m);
	}

	.em-peak-overview-row + .em-peak-overview-row {
		border-top: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-peak-overview-row-strongest {
		background: color-mix(in srgb, var(--color-warning) 7%, transparent);
	}

	.em-peak-overview-source > div,
	.em-peak-overview-metric {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
		min-width: 0;
	}

	.em-peak-overview-gauge :global(.linear-gauge) {
		flex: 1;
	}

	@container peak-overview (max-width: 560px) {
		.em-peak-overview-summary {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		.em-peak-overview-summary-item:first-child {
			grid-column: 1 / -1;
		}

		.em-peak-overview-summary-item:nth-child(n + 2) {
			border-top: 1px solid rgba(255, 255, 255, 0.12);
			border-left: 0;
		}

		.em-peak-overview-summary-item:nth-child(3) {
			border-left: 1px solid rgba(255, 255, 255, 0.12);
		}

		.em-peak-overview-row {
			grid-template-columns: minmax(0, 1fr) auto auto;
			gap: var(--spacing-s);
		}

		.em-peak-overview-row > :nth-child(4) {
			display: none;
		}

		.em-peak-overview-gauge {
			grid-column: 1 / -1;
		}
	}

	@container peak-overview (max-width: 360px) {
		.em-peak-overview-summary {
			grid-template-columns: minmax(0, 1fr);
		}

		.em-peak-overview-summary-item:first-child {
			grid-column: auto;
		}

		.em-peak-overview-summary-item:nth-child(3) {
			border-left: 0;
		}

		.em-peak-overview-row {
			grid-template-columns: minmax(0, 1fr) repeat(2, minmax(52px, auto));
		}
	}
</style>
